import { makeAutoObservable } from 'mobx';

import { MAX_CONVERSATION_HISTORY } from './consts';
import {
  getPreferredLanguage,
  isIncognitoMode,
  isValidName,
  loadSophieData,
  persistSophieData,
} from './helpers';
import {
  AssistantAction,
  AssistantMode,
  ChatMessage,
  ChatState,
  SophieData,
  SophieInstructions,
  SophieMessage,
  SophiePhase,
  VoiceState,
} from './types';

export class AssistantStore {
  userId = '';
  userName: string | undefined = undefined;
  language = 'English';
  lastInteraction: number | undefined = undefined;
  conversationHistory: SophieMessage[] = [];

  voiceState: VoiceState = 'idle';
  sophiePhase: SophiePhase = null;
  isMicMuted = false;
  voiceLevels: number[] | null = null;
  nameDetected = false;

  /** Current assistant mode: 'voice', 'text', or null (nothing selected) */
  assistantMode: AssistantMode = null;
  chatState: ChatState = 'idle';

  /** Shared message list — used by both voice and text modes */
  chatMessages: ChatMessage[] = [];
  currentAssistantMsgId: string | null = null;
  currentUserMsgId: string | null = null;

  /** Actions that require React hooks — registered from App via setAction */
  actions: Partial<Record<AssistantAction, () => void>> = {};

  constructor() {
    makeAutoObservable(this, { actions: false }, { autoBind: true });
    this.loadFromStorage();
  }

  setAction(action: AssistantAction, fn: () => void) {
    this.actions[action] = fn;
  }

  // ── Storage ───────────────────────────────────────────────────────────────

  loadFromStorage() {
    const saved = loadSophieData();
    if (!saved) return;

    if (saved.userId) this.userId = saved.userId;
    if (saved.userName && isValidName(saved.userName)) this.userName = saved.userName;
    if (saved.language) this.language = saved.language;
    if (saved.lastInteraction) this.lastInteraction = saved.lastInteraction;
    if (saved.conversationHistory) this.conversationHistory = saved.conversationHistory;
  }

  private persist(patch?: Partial<SophieData>) {
    const data: SophieData = {
      userId: this.userId,
      userName: this.userName,
      language: this.language,
      lastInteraction: this.lastInteraction,
      conversationHistory: this.conversationHistory,
      ...patch,
    };
    persistSophieData(data);
  }

  // ── User ID ───────────────────────────────────────────────────────────────

  ensureUserId(): string {
    if (this.userId) return this.userId;

    const uid = 'su_' + crypto.randomUUID().replace(/-/g, '').slice(0, 16);
    this.userId = uid;
    this.persist();
    console.log('[Sophie] Generated new user ID:', uid);
    return uid;
  }

  // ── User Name ─────────────────────────────────────────────────────────────

  setUserName(name: string) {
    if (!isValidName(name)) {
      console.warn('[Sophie] Rejected invalid name:', name);
      return false;
    }
    this.userName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    this.nameDetected = true;
    this.persist();
    return true;
  }

  // ── Voice State ───────────────────────────────────────────────────────────

  setVoiceState(state: VoiceState) {
    this.voiceState = state;
    if (state !== 'active') this.sophiePhase = null;
  }

  setSophiePhase(phase: SophiePhase) {
    this.sophiePhase = phase;
  }

  setIsMicMuted(muted: boolean) {
    this.isMicMuted = muted;
  }

  setVoiceLevels(levels: number[] | null) {
    this.voiceLevels = levels;
  }

  // ── Assistant Mode Toggle ─────────────────────────────────────────────────

  get isChatOpen(): boolean {
    return this.assistantMode === 'text';
  }

  setAssistantMode(mode: AssistantMode) {
    if (this.assistantMode === mode) return;
    this.assistantMode = mode;
    // Reset chat WS state when leaving text mode
    if (mode !== 'text') {
      this.chatState = 'idle';
    }
  }

  setChatState(state: ChatState) {
    this.chatState = state;
  }

  // ── Shared Chat Messages ──────────────────────────────────────────────────

  addChatMessage(msg: ChatMessage) {
    this.chatMessages.push(msg);
  }

  appendToChatMessage(id: string, delta: string) {
    const msg = this.chatMessages.find((m) => m.id === id);
    if (msg) msg.text += delta;
  }

  finalizeChatMessage(id: string) {
    const msg = this.chatMessages.find((m) => m.id === id);
    if (msg) msg.streaming = false;
  }

  clearChatMessages() {
    this.chatMessages = [];
    this.currentAssistantMsgId = null;
    this.currentUserMsgId = null;
  }

  /**
   * Ensure a streaming assistant message exists. Returns its id.
   * Used for Sophie's auto-greeting and any incoming text delta.
   */
  ensureAssistantMsg(): string {
    if (this.currentAssistantMsgId) return this.currentAssistantMsgId;

    const id = 'a_' + Date.now();
    this.currentAssistantMsgId = id;
    this.chatMessages.push({ id, role: 'assistant', text: '', ts: Date.now(), streaming: true });
    return id;
  }

  /**
   * Ensure a streaming user message exists (for voice STT). Returns its id.
   */
  ensureUserMsg(): string {
    if (this.currentUserMsgId) return this.currentUserMsgId;

    const id = 'u_' + Date.now();
    this.currentUserMsgId = id;
    this.chatMessages.push({ id, role: 'user', text: '', ts: Date.now(), streaming: true });
    return id;
  }

  /**
   * Finalize current assistant message. Returns its text for debug logging.
   */
  finalizeAssistantMsg(): string {
    const id = this.currentAssistantMsgId;
    if (!id) return '';

    this.finalizeChatMessage(id);
    const msg = this.chatMessages.find((m) => m.id === id);
    this.currentAssistantMsgId = null;
    return msg?.text?.trim() || '';
  }

  /**
   * Finalize current user message (voice STT done). Returns its text.
   */
  finalizeUserMsg(): string {
    const id = this.currentUserMsgId;
    if (!id) return '';

    this.finalizeChatMessage(id);
    const msg = this.chatMessages.find((m) => m.id === id);
    this.currentUserMsgId = null;
    return msg?.text?.trim() || '';
  }

  /**
   * Replace the text of the last user message in chatMessages.
   * Used when Haiku STT correction arrives after speech_stopped.
   */
  replaceLastUserText(original: string, corrected: string) {
    for (let i = this.chatMessages.length - 1; i >= 0; i--) {
      const msg = this.chatMessages[i];
      if (msg.role === 'user' && msg.text.trim() === original.trim()) {
        msg.text = corrected;
        return;
      }
    }
  }

  /**
   * Add a finalized news story as a separate assistant bubble.
   * Finalizes the current streaming assistant message first (the intro).
   */
  addNewsStoryMsg(storyId: number, text: string) {
    // Finalize the intro bubble if still streaming
    if (this.currentAssistantMsgId) {
      this.finalizeChatMessage(this.currentAssistantMsgId);
      this.currentAssistantMsgId = null;
    }
    const id = `news_${storyId}_${Date.now()}`;
    this.chatMessages.push({
      id,
      role: 'assistant',
      text,
      ts: Date.now(),
      streaming: false,
      isNews: true,
    });
  }

  // ── Conversation History ──────────────────────────────────────────────────

  saveMessage(role: 'user' | 'assistant', text: string) {
    if (!text.trim()) return;
    this.conversationHistory.push({ role, text: text.trim(), ts: Date.now() });
    if (this.conversationHistory.length > MAX_CONVERSATION_HISTORY) {
      this.conversationHistory.splice(
        0,
        this.conversationHistory.length - MAX_CONVERSATION_HISTORY
      );
    }
    this.lastInteraction = Date.now();
    this.persist();
  }

  touchLastInteraction() {
    this.lastInteraction = Date.now();
    this.persist();
  }

  // ── Sophie Instructions ───────────────────────────────────────────────────

  get timeSinceLastInteraction(): number {
    if (!this.lastInteraction) return Infinity;
    return Date.now() - this.lastInteraction;
  }

  buildInstructions(): SophieInstructions {
    const isIncognito = isIncognitoMode();
    const userName = this.userName;
    const language = isIncognito ? 'English' : getPreferredLanguage(this.language);
    const timeSince = this.timeSinceLastInteraction;
    const isActiveDialog = timeSince < 3 * 60 * 60 * 1000;
    const history = this.conversationHistory;
    const isFirstTime = !userName && history.length === 0;
    const isReturningNoName = !userName && history.length > 0;

    const nameCorrection = `\nIMPORTANT: If the user corrects their name or says "call me X" / "my name is X" / "no, I'm X", IMMEDIATELY accept it. Never argue about names.`;

    const userHour = new Date().getHours();
    const timeOfDay =
      userHour < 6
        ? 'late night'
        : userHour < 12
          ? 'morning'
          : userHour < 18
            ? 'afternoon'
            : 'evening';
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    let absenceNote = '';
    if (timeSince > 0 && timeSince !== Infinity) {
      const hours = Math.floor(timeSince / (60 * 60 * 1000));
      const days = Math.floor(hours / 24);
      if (days > 7)
        absenceNote = `User hasn't talked to you in over ${days} days — it's been a while!`;
      else if (days > 1) absenceNote = `User hasn't talked to you in ${days} days.`;
      else if (hours > 6) absenceNote = `User last talked to you ${hours} hours ago.`;
      else if (hours > 0) absenceNote = `User talked to you ${hours} hours ago (recent).`;
      else absenceNote = `User just talked to you recently (within the hour).`;
    }

    let pastContext = '';
    if (history.length > 0) {
      const lastTopics = history
        .slice(-6)
        .map((m) => `${m.role}: ${m.text}`)
        .join('\n');
      pastContext = `Previous conversation (do NOT repeat or summarize — just be aware of what you discussed):\n${lastTopics}`;
    }

    let greeting = '';

    if (isFirstTime) {
      greeting =
        `# GREETING CONTEXT
This is a BRAND NEW user — first time ever. You have never spoken with them.
Time: ${timeOfDay} (${userTz})

YOUR GREETING — follow these steps:
1. Say hi and your name (Sophie)
2. Briefly mention what you do — something like "I work at WOWMAX, I do DeFi research, trading, and travel to crypto conferences sometimes." Keep it casual and human. One sentence.
3. Ask their name

Keep it to 2-3 short sentences total. Sound like a real person chatting, not reading a script.
NEVER copy the instruction text word-for-word — rephrase it naturally each time.

After they tell you their name, react naturally and ask what they're interested in.` +
        nameCorrection;
    } else if (isReturningNoName) {
      greeting =
        `# GREETING CONTEXT
Returning user but you DON'T KNOW THEIR NAME. They already know who you are.
Time: ${timeOfDay} (${userTz})
${absenceNote}
${pastContext}

YOUR GREETING: Don't re-introduce yourself. Just say hi casually and ask what they'd like to be called. Work the name question in naturally — don't make it the whole point of the greeting.
Keep it to 1-2 sentences. Use time of day or trending news if it fits.` + nameCorrection;
    } else if (!isActiveDialog) {
      greeting =
        `# GREETING CONTEXT
Returning user. Their name is: ${userName}. You MUST use their name. NEVER ask for their name. NEVER introduce yourself.
Time: ${timeOfDay} (${userTz})
${absenceNote}
${pastContext}

YOUR GREETING: Pick from the GREETING STYLES in your system prompt. Keep it to 1-2 sentences.
Factors to weave in NATURALLY (use 1-2, not all):
- Time of day: "${timeOfDay}" → morning/evening greeting if it fits
- Absence: ${absenceNote ? absenceNote : 'N/A'}
- Trending news: reference a headline from TRENDING NEWS section if interesting
- Past chat: nod to what you discussed before IF relevant
- Style: vary it every time, never repeat the same pattern
NEVER say "What's going on in your crypto world today?" — this phrase is BANNED.` + nameCorrection;
    } else {
      greeting =
        `# GREETING CONTEXT
Active dialog continuation. User's name is: ${userName}. NEVER ask for their name. NEVER re-introduce yourself.
Continue the conversation naturally. Address them by name only if it fits.` + nameCorrection;
    }

    return {
      instructions: greeting,
      shouldGreetFirst: !isActiveDialog,
      isFirstTime,
      isReturningNoName,
      userName,
      language,
    };
  }
}
